import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, useReducedMotion, useDragControls } from "framer-motion";
import { X, Instagram, Mail, Youtube, Linkedin, Play } from "lucide-react";



/** ------------ Custom TikTok Icon (einfaches SVG) ------------ */
function TikTokIcon({ size = 20, className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
    >
      <path
        d="M15.5 4.5c.6 1.2 1.7 2.2 3 2.6v2.3c-1.3-.1-2.5-.5-3.6-1.2v5.9c0 2.7-2.2 4.9-4.9 4.9S5 16.8 5 14.1c0-2.4 1.7-4.3 4-4.8v2.4c-.9.4-1.5 1.2-1.5 2.2 0 1.3 1.1 2.4 2.4 2.4s2.4-1.1 2.4-2.4V4.5h3.2z"
        fill="currentColor"
      />
    </svg>
  );
}

/** ------------ Top-Bar ------------ */
function TopBar({ onLogoClick, activeLabel }) {
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeString = time.toLocaleTimeString("de-DE", {
    hour12: false,
  });

  return (
    <div className="fixed top-0 left-0 right-0 h-10 md:h-12 bg-neutral-900 flex items-center px-3 md:px-6 z-40 text-[var(--brand-blue)]">
      {/* Links: Logo + Name */}
      <button
        onClick={onLogoClick}
        className="group flex items-center min-w-0"
        aria-label="Zurück zum Desktop"
      >
        <span className="text-xs md:text-sm font-semibold tracking-[0.16em] uppercase group-hover:opacity-80">
          Y+
        </span>
      </button>


      {/* Mitte: aktiver Titel – auf Mobile ausgeblendet, damit nichts überlappt */}
      <div className="hidden md:block flex-1 text-center text-xs tracking-[0.18em] uppercase text-neutral-300">
        {activeLabel || ""}
      </div>
{/* Rechts: Social + Uhrzeit */}
<div className="ml-auto flex items-center gap-3 md:gap-4 text-xs md:text-sm justify-end">
  <div className="flex items-center gap-3">
    {/* Social Icons – Links deaktiviert */}
    <button type="button" aria-label="Instagram" className="hover:text-pink-400 cursor-default">
      <Instagram size={18} />
    </button>

    <button type="button" aria-label="TikTok" className="hover:text-cyan-300 cursor-default">
      <TikTokIcon size={18} />
    </button>

    <button type="button" aria-label="YouTube" className="hover:text-red-400 cursor-default">
      <Youtube size={18} />
    </button>

    <button type="button" aria-label="LinkedIn" className="hover:text-sky-300 cursor-default">
      <Linkedin size={18} />
    </button>

    <a
      href="mailto:Contact@yplus.co"
      aria-label="E-Mail"
      className="hover:text-neutral-100"
    >
      <Mail size={18} />
    </a>


    {/*
    Links (deaktiviert):
    <a href="https://www.instagram.com/soconstudio/" ...>...</a>
    <a href="https://www.tiktok.com/@soconstudio?lang=de-DE" ...>...</a>
    <a href="https://www.youtube.com/@SoconStudio" ...>...</a>
    <a href="https://www.linkedin.com/company/socongroup/" ...>...</a>
    <a href="mailto:Contact@yplus.co" ...>...</a>
    */}
  </div>

  <span className="tabular-nums text-xs md:text-sm">{timeString}</span>
</div>


    </div>
  );
}

/** ------------ Team-Inhalt ------------ */
function TeamContent({ onClose }) {
  return (
    <div className="h-full bg-[#e5e5e5] flex flex-col">
      {/* TextEdit Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#dadada] border-b border-[#c8c8c8] text-[11px] text-neutral-700">
        <div className="flex items-center gap-2">
          {/* Traffic Lights */}
          <div className="flex items-center gap-1.5 mr-3">
            <button
              type="button"
              onClick={onClose}
              aria-label="Fenster schließen"
              className="w-3 h-3 rounded-full bg-[#ff5f57] border border-black/10 focus:outline-none"
            />
            <span className="w-3 h-3 rounded-full bg-[#febc2e] border border-black/10" />
            <span className="w-3 h-3 rounded-full bg-[#28c840] border border-black/10" />
          </div>
          {/* Font Dropdowns (nur optisch) */}
          <div className="flex items-center gap-2">
            <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
              Helvetica Neue
            </div>
            <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
              Regular
            </div>
            <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
              18
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-70">
          <div className="w-5 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
          <div className="w-5 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
          <div className="w-6 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
        </div>
      </div>

      {/* Dokumentbereich */}
      <div className="flex-1 overflow-auto px-10 py-6 bg-white text-[13px] leading-relaxed text-neutral-900 custom-scroll">
        <p className="mb-4">
          Y+ is where creative identity meets organized marketing. With roots in
          the music and lifestyle industries and experience in business and
          management, we combine cultural awareness, aesthetics, and strategy.
          We develop brands, produce content, and create campaigns that are
          modern, structured, and effective. Simple, clear, and professional.
        </p>
        <p className="mb-4">
          <strong>Younes Mo</strong>
          <br />
          Originally coming from the finance world, Younes worked five years in
          banking, most recently in portfolio management. He brings structure,
          analysis, and strategic communication to marketing and content
          production. After launching his own agency, his focus shifted to
          campaign development, video production, and clear brand communication.
          He combines financial precision with creative output — turning ideas
          into organized, measurable, and visually compelling campaigns.
        </p>
        <p className="mb-4">
          <strong>Younes Me</strong>
          <br />
          Coming from the music industry, Younes works across creative brand
          building and social media. He develops artists, restaurants, hotels
          and personal brands — guiding projects from concept and product
          development to launch, content and distribution. His strengths are
          aesthetic direction, cultural awareness and a sharp feel for what’s
          current, translating strategy into visual identity, storytelling and
          brand relations that move audiences and deliver results.
        </p>
        <div className="mt-10 flex justify-center">
        <img
          src="/images/catchusifyoucan.png"
          alt="Catch us if you can"
          className="max-w-full md:max-w-[240px] opacity-90"
          draggable={false}
        />
      </div>
      </div>
    </div>
  );
}

/** ------------ Profiles-Fenster ------------ */
/** ------------ Profiles-Fenster ------------ */
/** ------------ Profiles-Fenster ------------ */
function ProfilesContent({ onClose }) {
  const items = [
    {
      key: "instagram",
      label: "Instagram",
      href: "https://www.instagram.com/soconstudio/",
      Icon: Instagram,
    },
    {
      key: "tiktok",
      label: "TikTok",
      href: "https://www.tiktok.com/@soconstudio?lang=de-DE",
      Icon: TikTokIcon,
    },
    {
      key: "youtube",
      label: "YouTube",
      href: "https://www.youtube.com/@SoconStudio",
      Icon: Youtube,
    },

    {
      key: "linkedin",
      label: "LinkedIn",
      href: "https://www.linkedin.com/company/socongroup/",
      Icon: Linkedin,
    },
    {
      key: "mail",
      label: "E-Mail",
      href: "mailto:Contact@yplus.co",
      Icon: Mail,
    },
  ];

  // eigene Positionen für Mobile & Desktop
  const mobilePositions = [
    { top: "6%", left: "10%" },
    { top: "22%", right: "10%" },
    { top: "40%", left: "14%" },
    { top: "58%", right: "16%" },
    { top: "74%", left: "20%" },
    { top: "30%", left: "55%" },
  ];

  const desktopPositions = [
    { top: "10%", left: "6%" },
    { top: "22%", right: "10%" },
    { top: "38%", left: "20%" },
    { top: "52%", right: "16%" },
    { top: "68%", left: "50%" },
    { top: "30%", left: "55%" },
  ];

  const isMobile =
    typeof window !== "undefined" && window.innerWidth <= 768;

  return (
    <div className="h-full bg-[#e5e5e5] flex flex-col">
      {/* Toolbar wie TextEdit */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#dadada] border-b border-[#c8c8c8] text-[11px] text-neutral-700">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 mr-3">
            <button
              type="button"
              onClick={onClose}
              aria-label="Fenster schließen"
              className="w-3 h-3 rounded-full bg-[#ff5f57] border border-black/10 focus:outline-none"
            />
            <span className="w-3 h-3 rounded-full bg-[#febc2e] border border-black/10" />
            <span className="w-3 h-3 rounded-full bg-[#28c840] border border-black/10" />
          </div>

          <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
            Helvetica Neue
          </div>
          <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
            Regular
          </div>
          <div className="px-2 py-0.5 bg-white border border-[#bcbcbc] rounded text-[10px]">
            18
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-70">
          <div className="w-5 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
          <div className="w-5 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
          <div className="w-6 h-4 bg-white/70 border border-[#bcbcbc] rounded" />
        </div>
      </div>

      {/* Inhalt: Icons groß, clean, verteilt */}
      <div className="flex-1 bg-white px-6 py-8 md:px-10 md:py-10">
        <div className="max-w-3xl mx-auto h-full flex flex-col">
          <div className="mb-6 text-center text-[11px] md:text-xs uppercase tracking-[0.18em] text-neutral-500">
            Social Profiles
          </div>

          {/* relativer Container / Playground */}
          <div className="relative flex-1 min-h-[360px] md:min-h-[380px]">
            {items.map(({ key, label, href, Icon }, index) => {
              const pos = (isMobile ? mobilePositions : desktopPositions)[index] || {
                top: "50%",
                left: "50%",
              };

              return (
                <div
                  key={key}
                  style={pos}
                  className="absolute flex flex-col items-center gap-2 text-center select-none"
                  aria-label={label}
                >
                  <Icon
                    size={isMobile ? 52 : 64}
                    className="text-neutral-900 opacity-95"
                  />
                  <span className="text-[10px] md:text-[11px] tracking-wide text-neutral-700">
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


/** ------------ PageShell mit Didot-Heading-Logik ------------ */
function PageShell({ title, onBack, children }) {
  const serifTitles = ["MAGAZINE", "AGB", "DATENSCHUTZ", "IMPRESSUM"];
  const isSerifPage = serifTitles.includes(
    (title || "").toUpperCase()
  );

  return (
    <div className="absolute inset-x-0 top-10 md:top-12 bottom-0 bg-black text-white overflow-auto custom-scroll px-4 md:px-16 lg:px-32 pt-8 md:pt-10 pb-16 page-shell">
      <button
        onClick={onBack}
        className="mb-8 text-sm md:text-base text-neutral-300 hover:text-white"
      >
        Back to <span className="italic">"desktop"</span>
      </button>

      {title && (
       <h1
          className={`text-3xl md:text-5xl text-center mb-12 ${
            isSerifPage ? "page-serif-title" : ""
          }`}
        >
          {title}
        </h1>

      )}

      <div className={isSerifPage ? "page-serif-text" : ""}>
        {children}
      </div>

    </div>
  );
}

/** ------------ Magazin-Seite ------------ */
function MagazinePage({ sections, onBack }) {
  return (
    <PageShell title="MAGAZINE" onBack={onBack}>
      <MagazineGrid sections={sections} />
    </PageShell>
  );
}

function VideoTile({
  src,
  trimEndSeconds = 0,
  objectPosition = "50% 20%",
  className = "",
}) {
  const videoRef = useRef(null);

  const [duration, setDuration] = useState(null);
  const [isReady, setIsReady] = useState(false);   // erstes Frame ist sichtbar
  const [isPlaying, setIsPlaying] = useState(false);

  // erstes Frame vorbereiten (damit nicht schwarz)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const primeFirstFrame = async () => {
      try {
        // Manche Browser brauchen einen minimalen Seek, damit ein Frame gerendert wird
        v.currentTime = 0.01;
        v.pause();
        setIsReady(true);
      } catch {
        // Fallback: auch wenn seek blockt, zeigen wir zumindest die UI
        setIsReady(true);
      }
    };

    const onLoadedMetadata = () => {
      setDuration(v.duration || null);
    };

    const onLoadedData = () => {
      // genug Daten für erstes Frame
      primeFirstFrame();
    };

    const onTimeUpdate = () => {
      if (!trimEndSeconds || !duration) return;
      const stopAt = Math.max(0, duration - trimEndSeconds);
      if (v.currentTime >= stopAt) {
        v.pause();
        v.currentTime = 0.01;
        setIsPlaying(false);
      }
    };

    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("timeupdate", onTimeUpdate);

    // wichtig: nicht automatisch starten
    v.pause();

    return () => {
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [src, trimEndSeconds, duration]);

  const play = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      // Start immer sauber ab Anfang
      v.currentTime = 0;
      await v.play();
      setIsPlaying(true);
    } catch {
      // wenn Browser play blockt, bleibt overlay – user klickt ggf. nochmal
    }
  };

  const stop = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0.01;
    setIsPlaying(false);
  };

  return (
    <div className={`relative w-full h-full ${className}`}>
      <video
        ref={videoRef}
        className={`w-full h-full object-cover ${isReady ? "opacity-100" : "opacity-0"}`}
        src={src}
        muted
        playsInline
        preload="auto"
        controls={false}
        style={{ objectFit: "cover", objectPosition }}
      />

      {/* Play Overlay: nur wenn nicht playing */}
      {!isPlaying && (
        <button
          type="button"
          onClick={play}
          aria-label="Video abspielen"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="flex items-center justify-center w-12 h-12 rounded-full backdrop-blur-sm transition"
            style={{
              backgroundColor: "rgba(0,0,0,0.45)",
              border: "1px solid var(--brand-blue)",
            }}
          >
            <Play style={{ color: "var(--brand-blue)" }} size={22} />
          </span>
        </button>
      )}

      {/* Optional: wenn playing, klick = stop/restart */}
      {isPlaying && (
        <button
          type="button"
          onClick={stop}
          aria-label="Video stoppen"
          className="absolute top-3 right-3"
        >
          <span className="px-2 py-1 rounded-md bg-black/45 backdrop-blur-sm text-xs text-white">
            Stop
          </span>
        </button>
      )}
    </div>
  );
}



/** ------------ Magazin-Grid ------------ */
/** ------------ Magazin-Grid (IG-Feed Reihenfolge) ------------ */
function MagazineGrid({ sections }) {
  const images = [];
  const videos = [];

  sections.forEach((sec) => {
    // Bilder in Reihenfolge sammeln
    sec.images.forEach((src) => images.push({ type: "image", src }));

    // Videos in Reihenfolge sammeln
    if (sec.video) {
      videos.push({
        type: "video",
        src: sec.video.src,
        poster: sec.video.poster,
        id: sec.video.id,
        trimEndSeconds: sec.video.trimEndSeconds,
        objectPosition: sec.video.objectPosition,
      });
    }
  });

  const imgQueue = [...images];
  const vidQueue = [...videos];
  const items = [];

  // Wir bauen 3er-Reihen wie in IG:
  // [Foto, Video, Foto] solange Videos da sind
  // Danach nur Fotos auffüllen
  while (imgQueue.length > 0 || vidQueue.length > 0) {
    // links
    const left = imgQueue.shift();

    // mitte: Video wenn verfügbar, sonst Foto
    const middle = vidQueue.length > 0 ? vidQueue.shift() : imgQueue.shift();

    // rechts
    const right = imgQueue.shift();

    if (left) items.push(left);
    if (middle) items.push(middle);
    if (right) items.push(right);

    // Falls keine Bilder mehr da sind, aber noch Videos: Videos „normal“ weiter ausgeben
    // (passiert selten, aber verhindert Endlos-Schleifen / Lücken)
    if (!left && !right && middle?.type === "video" && imgQueue.length === 0) {
      while (vidQueue.length > 0) items.push(vidQueue.shift());
      break;
    }
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
      {items.map((item, idx) => (
        <motion.div
          key={`${item.type}-${item.src}-${idx}`}
          className="relative rounded-xl overflow-hidden bg-black aspect-square"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
        >
          {item.type === "image" ? (
            <img
              src={item.src}
              alt={`Magazin ${idx + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
              // optional: falls ein Bild doch fehlt -> Kachel ausblenden statt Alt-Text sichtbar
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <VideoTile
              src={item.src}
              poster={item.poster}
              trimEndSeconds={item.trimEndSeconds || 0}
              objectPosition={item.objectPosition || "50% 20%"}
            />
          )}
        </motion.div>
      ))}
    </div>
  );
}


/** ------------ Desktop Icon ------------ */
function DesktopIcon({ onClick, icon, label, style }) {
  return (
    <button
      onClick={onClick}
      style={style}
      className="absolute flex flex-col items-center hover:opacity-95 focus:outline-none desktop-icon"
      aria-label={label}
    >
      <motion.img
        src={icon}
        alt={label}
        className="h-32 w-auto max-w-[140px] mb-2 select-none object-contain"
        draggable={false}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      />
      <span className="text-white text-sm font-semibold drop-shadow-sm">
        {label}
      </span>
    </button>
  );
}

/** ------------ Mobile Desktop Menü (Icons) ------------ */
/** ------------ Mobile Desktop Menü (Icons „durcheinander“) ------------ */
function MobileDesktopMenu({ onOpen }) {
  const items = [
    {
      key: "magazin",
      label: "magazine",
      icon: "/images/applefolder.png",
    },
    {
      key: "team",
      label: "about us",
      icon: "/images/teamicon.png",
    },
    {
      key: "profiles",
      label: "Profiles",
      icon: "/images/applefolder.png",
    },
    {
      key: "agb",
      label: "agb",
      icon: "/images/txticon.png",
    },
    {
      key: "datenschutz",
      label: "datenschutz",
      icon: "/images/txticon.png",
    },
    {
      key: "impressum",
      label: "impressum",
      icon: "/images/txticon.png",
    },
  ];

  // Feste Positionen für „durcheinander“, aber ohne Überlappung
  const positions = [
    { top: "12%", left: "8%" },
    { top: "16%", right: "10%" },
    { top: "44%", left: "14%" },
    { top: "60%", right: "16%" },
    { top: "72%", left: "22%" },
    { top: "36%", left: "65%" },
  ];

  return (
    <div className="absolute inset-x-0 top-10 bottom-[60px] md:hidden overflow-hidden mobile-desktop-menu">
      {/* relativer Container für die absolut positionierten Icons */}
      <div className="relative w-full h-full pt-6 pb-10">
        {items.map(({ key, label, icon }, index) => {
          const pos = positions[index] || { top: "50%", left: "50%" };

          return (
            <button
              key={key}
              onClick={() => onOpen(key)}
              style={pos}
              className="absolute flex flex-col items-center text-white focus:outline-none"
            >
              <img
                src={icon}
                alt={label}
                className="h-20 w-auto mb-2 object-contain drop-shadow-md"
                draggable={false}
              />
              <span className="text-[11px] font-semibold text-center leading-tight drop-shadow-sm">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


/** ------------ Fensterrahmen ------------ */
function WindowFrame({
  title,
  children,
  onClose,
  desktopRef,
  isMobile,
  width = "70%",
  height = "70%",
}) {
  const shouldReduceMotion = useReducedMotion();
  const dragControls = useDragControls();

  if (isMobile) {
    return (
      <PageShell title={title} onBack={onClose}>
        <div className="bg-white text-neutral-900 rounded-xl max-w-3xl mx-auto p-0 overflow-hidden">
          {children}
        </div>
      </PageShell>
    );
  }

  return (
    <motion.div
      drag
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={desktopRef}
      dragElastic={0.08}
      className="absolute z-30 rounded-xl overflow-hidden shadow-xl border border-neutral-300 bg-white window-frame"
      style={{
        top: "50%",
        left: "50%",
        width,
        height,
      }}
      initial={{
        opacity: 0,
        scale: shouldReduceMotion ? 1 : 0.92,
        x: "-50%",
        y: "-50%",
      }}
      animate={{
        opacity: 1,
        scale: 1,
        x: "-50%",
        y: "-50%",
      }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      onPointerDown={(e) => dragControls.start(e)}
    >
      <div className="flex justify-between items-center px-4 py-2 bg-neutral-100 text-neutral-900 border-b border-neutral-300 window-header">
        <span className="text-sm">{title}</span>
        <button
          aria-label="Fenster schließen"
          onClick={onClose}
          className="rounded-md p-1 hover:bg-neutral-200"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden bg-white window-content">
        {children}
      </div>
    </motion.div>
  );
}

/** ------------ Spezielles TextEdit-Fenster ------------ */
function TextEditWindow({ children, desktopRef }) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      drag
      dragConstraints={desktopRef}
      dragElastic={0.08}
      className="absolute z-30 rounded-xl overflow-hidden shadow-xl border border-neutral-300 bg-white"
      style={{
        top: "50%",
        left: "50%",
        width: "60%",
        height: "70%",
      }}
      initial={{
        opacity: 0,
        scale: shouldReduceMotion ? 1 : 0.92,
        x: "-50%",
        y: "-50%",
      }}
      animate={{
        opacity: 1,
        scale: 1,
        x: "-50%",
        y: "-50%",
      }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
    >
      {children}
    </motion.div>
  );
}

/** ------------ Logos ------------ */
const clientLogos = [
  "/logos/client1.png",
  "/logos/client2.png",
  "/logos/client3.png",
  "/logos/client4.png",
  "/logos/client5.png",
  "/logos/client6.png",
  "/logos/client7.png",
  "/logos/client8.png",
  "/logos/client9.png",
  "/logos/client10.png",
  "/logos/client11.png",
  "/logos/client12.png",
  "/logos/client13.png",
  "/logos/client14.png",
  "/logos/client15.png",
  "/logos/client16.png",
  "/logos/client17.png",
  "/logos/client18.png",
  "/logos/client19.png",
  "/logos/client20.png",
  "/logos/client21.png",
  "/logos/client22.png",
  "/logos/client23.png",
  "/logos/client24.png",
  "/logos/client25.png",
  "/logos/client26.png",
  "/logos/client27.png",
  "/logos/client28.png",
  "/logos/client29.png"
];

/** =================== APP =================== */
function App() {
  const [view, setView] = useState("desktop"); // "desktop" | "page"
  const [page, setPage] = useState(null); // magazin, agb, datenschutz, impressum, team, profiles
  const [windows, setWindows] = useState({
    team: false,
    profiles: false,
  });

    // Browser-Back innerhalb der App abfangen
  useEffect(() => {
    // Initialen State setzen
    window.history.replaceState(
      { view: "desktop", page: null },
      ""
    );

    const onPopState = (event) => {
      const state = event.state;

      if (state?.view) {
        setView(state.view);
        setPage(state.page ?? null);
        setWindows({ team: false, profiles: false });
      } else {
        // Fallback: nicht aus der App raus
        setView("desktop");
        setPage(null);
        setWindows({ team: false, profiles: false });
        window.history.pushState(
          { view: "desktop", page: null },
          ""
        );
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);


  const windowKeys = ["team", "profiles"];

  // Mobile-Erkennung
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Medienlisten
  const photos = useMemo(
    () => Array.from({ length: 21 }, (_, i) => `/magazin/foto${i + 1}.jpeg`),
    []
  );

  const videos = [
  { id: "v1", src: "/videos/video1.mov", objectPosition: "50% 80%" },

  // Zeitung: trimmen (letzte Sekunden weg) + Fokus etwas höher
  {
    id: "newspaper",
    src: "/videos/video2.mp4",
    trimEndSeconds: 2.0,
    objectPosition: "50% 20%",
  },

  { id: "v3", src: "/videos/video3.mp4", objectPosition: "50% 20%" },
  { id: "v4", src: "/videos/video4.mov", objectPosition: "50% 20%" },
  { id: "v5", src: "/videos/video5.mp4", objectPosition: "50% 20%" },
  { id: "v6", src: "/videos/video6.mov", objectPosition: "50% 20%" },
  { id: "v7", src: "/videos/video7.mp4", objectPosition: "50% 20%" },
  { id: "v8", src: "/videos/video8.mov", objectPosition: "50% 20%" },
  { id: "v9", src: "/videos/video9.mp4", objectPosition: "50% 20%" },
];



  const sections = useMemo(() => {
    const list = [];
    for (let i = 0; i < videos.length; i++) {
      const start = i * 6;
      const imgs = photos.slice(start, start + 6);
      list.push({
        images: imgs,
        video: videos[i],
        variant: i % 2 === 0 ? "A" : "B",
      });
    }
    return list;
  }, [photos, videos]);

  const desktopRef = useRef(null);

  const bgVideoSrc = isMobile
    ? "/videos/desktop-bg-mobile.mp4"
    : "/videos/desktop-bg.mp4";

  const goDesktop = () => {
    setView("desktop");
    setPage(null);
    setWindows({ team: false, profiles: false });

    window.history.pushState(
      { view: "desktop", page: null },
      ""
    );
  };


  const openPage = (name) => {
  setWindows({ team: false, profiles: false });
  setView("page");
  setPage(name);

  window.history.pushState(
    { view: "page", page: name },
    ""
  );
};


  // Single-Window-Logik
  const openWindow = (name) => {
    setView("desktop");
    setPage(null);
    const next = windowKeys.reduce((acc, key) => {
      acc[key] = key === name;
      return acc;
    }, {});
    setWindows(next);
  };

  const closeWindow = (name) => {
    setWindows((prev) => ({ ...prev, [name]: false }));
  };

  const handleOpenSection = (key) => {
    if (isMobile) {
      openPage(key);
      return;
    }
    if (key === "team" || key === "profiles") {
      openWindow(key);
    } else {
      openPage(key);
    }
  };

  // aktiver Titel für Topbar
  let activeLabel = "";
  if (view === "desktop") {
    if (windows.team) activeLabel = "about us";
    else if (windows.profiles) activeLabel = "Profiles";
  } else if (view === "page") {
    switch (page) {
      case "magazin":
        activeLabel = "MAGAZINE";
        break;
      case "agb":
        activeLabel = "AGB";
        break;
      case "datenschutz":
        activeLabel = "DATENSCHUTZ";
        break;
      case "impressum":
        activeLabel = "IMPRESSUM";
        break;
      case "team":
        activeLabel = "about us";
        break;
      case "profiles":
        activeLabel = "Profiles";
        break;
      default:
        activeLabel = "";
    }
  }

  return (
    <div
      ref={desktopRef}
      className="relative w-screen h-screen overflow-hidden bg-black text-white"
    >
      {/* Hintergrundvideo nur im Desktop-View */}
      {view === "desktop" && (
        <>
          <video
            key={bgVideoSrc}
            className="absolute inset-0 w-full h-full object-cover"
            src={bgVideoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 pointer-events-none bg-black/20" />
          <div className="absolute inset-0 pointer-events-none [background-image:radial-gradient(transparent,rgba(0,0,0,0.25))]" />
        </>
      )}

      {/* Top-Bar */}
      <TopBar onLogoClick={goDesktop} activeLabel={activeLabel} />

      {/* DESKTOP-VIEW */}
      {view === "desktop" && (
        <>
          {/* Icons „unordentlich“ verteilt auf Desktop */}
          <div className="relative z-20 hidden md:block w-full h-full pt-16">
            <DesktopIcon
              onClick={() => handleOpenSection("magazin")}
              icon="/images/applefolder.png"
              label="magazine"
              style={{ top: "18%", left: "6%" }}
            />
            <DesktopIcon
              onClick={() => handleOpenSection("impressum")}
              icon="/images/txticon.png"
              label="impressum"
              style={{ top: "8%", left: "24%" }}
            />
            <DesktopIcon
              onClick={() => handleOpenSection("team")}
              icon="/images/teamicon.png"
              label="about us"
              style={{ top: "12%", right: "25%" }}
            />
            <DesktopIcon
              onClick={() => handleOpenSection("agb")}
              icon="/images/txticon.png"
              label="agb"
              style={{ bottom: "26%", left: "10%" }}
            />
            <DesktopIcon
              onClick={() => handleOpenSection("datenschutz")}
              icon="/images/txticon.png"
              label="datenschutz"
              style={{ bottom: "20%", right: "14%" }}
            />
            <DesktopIcon
              onClick={() => handleOpenSection("profiles")}
              icon="/images/applefolder.png"
              label="profiles"
              style={{ top: "24%", right: "6%" }}
            />
          </div>

          {/* Mobile „Desktop“-Icons */}
          <MobileDesktopMenu onOpen={handleOpenSection} />

          {/* Fenster (Desktop) */}
          {windows.team && !isMobile && (
            <TextEditWindow desktopRef={desktopRef}>
              <TeamContent onClose={() => closeWindow("team")} />
            </TextEditWindow>
          )}

          {windows.profiles && !isMobile && (
            <TextEditWindow desktopRef={desktopRef}>
              <ProfilesContent onClose={() => closeWindow("profiles")} />
            </TextEditWindow>
          )}

          {/* Footer-Logos – Höhe in CSS + inline style abgesichert */}
          <div
            className="fixed bottom-0 left-0 right-0 w-full z-10 marquee"
          >
            <div className="marquee__track">
              {clientLogos.concat(clientLogos).map((src, i) => (
                <div className="marquee__item" key={i}>
                  <img src={src} alt={`Client Logo ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* PAGE-VIEW */}
      {view === "page" && (
        <>
          {page === "magazin" && (
            <MagazinePage sections={sections} onBack={goDesktop} />
          )}

         {page === "agb" && (
          <PageShell title="AGB" onBack={goDesktop}>
            <div className="max-w-3xl mx-auto leading-relaxed text-sm md:text-base space-y-6 text-left">

              {/* ********** DEUTSCH ********** */}
              <h2 className="text-lg md:text-xl font-semibold text-center mb-4">
                Allgemeine Gesch&auml;ftsbedingungen (DE)
              </h2>

              <p>
                Die nachfolgenden Allgemeinen Gesch&auml;ftsbedingungen (&bdquo;AGB&ldquo;) gelten f&uuml;r alle
                Vertr&auml;ge zwischen <strong>Y+ &ndash; a Brand from Socon</strong>, Socon UG (haftungsbeschr&auml;nkt),
                Hanauer Landstra&szlig;e 521, 60386 Frankfurt am Main (nachfolgend &bdquo;Agentur&ldquo;, &bdquo;wir&ldquo; oder &bdquo;uns&ldquo;)
                und ihren Kunden (nachfolgend &bdquo;Kunde&ldquo;).
              </p>

              <h3 className="font-semibold mt-4">1. Geltungsbereich</h3>
              <p>
                Diese AGB gelten f&uuml;r alle von uns erbrachten Leistungen in den Bereichen Branding, Konzeption,
                Content-Produktion (Foto, Video, Social Media), Kampagnenentwicklung, Strategieberatung und
                &auml;hnliche Kreativ- und Beratungsleistungen. Abweichende Bedingungen des Kunden werden nur
                Vertragsbestandteil, wenn wir ihrer Geltung ausdr&uuml;cklich schriftlich zustimmen.
              </p>

              <h3 className="font-semibold mt-4">2. Vertragsschluss</h3>
              <p>
                Unsere Angebote sind freibleibend und unverbindlich. Ein Vertrag kommt zustande, wenn der Kunde
                unser Angebot schriftlich (z.&nbsp;B. per E-Mail) best&auml;tigt oder wir mit der Leistungserbringung
                auf ausdr&uuml;ckliche Anweisung beginnen.
              </p>

              <h3 className="font-semibold mt-4">3. Leistungen der Agentur</h3>
              <p>
                Der konkrete Leistungsumfang ergibt sich aus dem jeweiligen Angebot, Leistungs- oder Projektvertrag.
                Unsere Leistungen sind grunds&auml;tzlich individuell und projektbasiert. Anpassungen, Erweiterungen oder
                Zusatzleistungen, die &uuml;ber den urspr&uuml;nglich vereinbarten Umfang hinausgehen, werden gesondert verg&uuml;tet.
              </p>

              <h3 className="font-semibold mt-4">4. Mitwirkungspflichten des Kunden</h3>
              <p>
                Der Kunde stellt alle f&uuml;r die Leistungserbringung notwendigen Informationen, Materialien und Zug&auml;nge
                rechtzeitig und vollst&auml;ndig zur Verf&uuml;gung (z.&nbsp;B. Briefings, Bilder, Logos, Texte, Zugangsdaten zu
                Accounts). Verz&ouml;gerungen, die auf mangelnde oder versp&auml;tete Mitwirkung zur&uuml;ckzuf&uuml;hren sind, gehen zu
                Lasten des Kunden und k&ouml;nnen zu Verschiebungen von Fristen und Mehrkosten f&uuml;hren.
              </p>

              <h3 className="font-semibold mt-4">5. Verg&uuml;tung und Zahlungsbedingungen</h3>
              <p>
                Alle Preise werden individuell vereinbart und verstehen sich zuzüglich der jeweils geltenden
                gesetzlichen Umsatzsteuer, sofern nicht ausdr&uuml;cklich anders angegeben.
              </p>
              <p>
                Die Verg&uuml;tung ist, sofern nichts anderes vereinbart wurde, innerhalb von{" "}
                <strong>14 Tagen</strong> nach Rechnungsstellung ohne Abzug zur Zahlung f&auml;llig. Die Zahlung erfolgt per
                &Uuml;berweisung auf das in der Rechnung angegebene Konto.
              </p>
              <p>
                Wir sind berechtigt, angemessene Abschlagszahlungen oder Vorsch&uuml;sse (z.&nbsp;B. Projektanzahlung) zu verlangen,
                insbesondere bei umfangreichen oder l&auml;ngerfristigen Projekten.
              </p>

              <h3 className="font-semibold mt-4">6. Nutzungsrechte</h3>
              <p>
                S&auml;mtliche Urheber- und Leistungsschutzrechte an den von uns geschaffenen Werken (z.&nbsp;B. Konzepte,
                Designs, Fotos, Videos, Texte, Grafiken) verbleiben grunds&auml;tzlich bei uns, sofern nichts anderes
                vereinbart wurde.
              </p>
              <p>
                Der Kunde erh&auml;lt nach vollst&auml;ndiger Zahlung der vereinbarten Verg&uuml;tung ein{" "}
                <strong>einfaches, nicht exklusives, zeitlich und r&auml;umlich unbeschr&auml;nktes Nutzungsrecht</strong> f&uuml;r den
                vertraglich vereinbarten Zweck. Eine Nutzung &uuml;ber den vereinbarten Zweck hinaus, eine Bearbeitung oder
                Weitergabe der Inhalte an Dritte bedarf unserer vorherigen schriftlichen Zustimmung.
              </p>
              <p>
                Bis zur vollst&auml;ndigen Bezahlung verbleiben alle Rechte ausschlie&szlig;lich bei uns; der Kunde ist vor
                vollst&auml;ndiger Zahlung nicht berechtigt, die Inhalte zu nutzen oder zu ver&ouml;ffentlichen.
              </p>

              <h3 className="font-semibold mt-4">7. Abnahme und Korrekturen</h3>
              <p>
                Nach Bereitstellung der vereinbarten Leistungen ist der Kunde verpflichtet, diese innerhalb einer
                angemessenen Frist zu pr&uuml;fen und etwaige M&auml;ngel schriftlich geltend zu machen. Erfolgt innerhalb von
                7 Tagen nach &Uuml;bergabe keine R&uuml;ckmeldung, gelten die Leistungen als abgenommen.
              </p>
              <p>
                Soweit nicht anders vereinbart, umfasst der Angebotspreis in der Regel eine angemessene Anzahl an
                Korrekturschleifen zur Feinanpassung. Zus&auml;tzliche Korrekturen oder grundlegende Konzept&auml;nderungen,
                die &uuml;ber den urspr&uuml;nglichen Projektumfang hinausgehen, werden nach Aufwand gesondert berechnet.
              </p>

              <h3 className="font-semibold mt-4">8. Haftung</h3>
              <p>
                Wir haften unbeschr&auml;nkt f&uuml;r Vorsatz und grobe Fahrl&auml;ssigkeit sowie f&uuml;r Verletzungen von Leben,
                K&ouml;rper oder Gesundheit. Bei leichter Fahrl&auml;ssigkeit haften wir nur bei Verletzung wesentlicher
                Vertragspflichten (Kardinalpflichten) und nur in H&ouml;he des typischerweise vorhersehbaren Schadens.
              </p>
              <p>
                Eine Haftung f&uuml;r mittelbare Sch&auml;den, entgangenen Gewinn oder nicht vorhersehbare Folgesch&auml;den ist
                ausgeschlossen, soweit gesetzlich zul&auml;ssig.
              </p>

              <h3 className="font-semibold mt-4">9. Referenznennung</h3>
              <p>
                Wir sind berechtigt, den Kunden und das Projekt in angemessener Form als Referenz auf unserer
                Website, in Social-Media-Kan&auml;len oder in Pr&auml;sentationen zu nennen und dabei auch erstellte Arbeiten
                (z.&nbsp;B. Screenshots, Bilder, Videos) zu zeigen, sofern der Kunde dem nicht ausdr&uuml;cklich widerspricht.
              </p>

              <h3 className="font-semibold mt-4">10. K&uuml;ndigung</h3>
              <p>
                Laufende Projektvertr&auml;ge k&ouml;nnen aus wichtigem Grund von beiden Seiten ohne Einhaltung einer Frist
                gek&uuml;ndigt werden. Eine ordentliche K&uuml;ndigung ist nur m&ouml;glich, sofern dies vertraglich vereinbart wurde.
              </p>
              <p>
                Im Falle einer K&uuml;ndigung sind vom Kunden alle bis dahin angefallenen Leistungen und Kosten zu
                verg&uuml;ten. Bereits einger&auml;umte Rechte fallen im Zweifel an uns zur&uuml;ck, sofern keine vollst&auml;ndige
                Verg&uuml;tung erfolgt ist.
              </p>

              <h3 className="font-semibold mt-4">11. Schlussbestimmungen</h3>
              <p>
                Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
                Gerichtsstand f&uuml;r Kaufleute ist, soweit gesetzlich zul&auml;ssig, Frankfurt am Main.
              </p>
              <p>Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, bleibt die Wirksamkeit der &uuml;brigen Bestimmungen unber&uuml;hrt.</p>

              <p>Stand: November 2025</p>

              <hr className="my-10 border-neutral-300" />

              {/* ********** ENGLISCH ********** */}
              <h2 className="text-lg md:text-xl font-semibold text-center mb-4">
                General Terms &amp; Conditions (EN)
              </h2>

              <p>
                The following General Terms and Conditions (&quot;Terms&quot;) apply to all agreements between{" "}
                <strong>Y+ &ndash; a Brand from Socon</strong>, Socon UG (haftungsbeschr&auml;nkt), Hanauer Landstra&szlig;e 521,
                60386 Frankfurt am Main, Germany (hereinafter &quot;Agency&quot;, &quot;we&quot; or &quot;us&quot;) and its clients
                (hereinafter &quot;Client&quot;).
              </p>

              <h3 className="font-semibold mt-4">1. Scope</h3>
              <p>
                These Terms apply to all services provided by us in the fields of branding, concept development,
                content production (photo, video, social media), campaign development, strategy consulting and related
                creative and consulting services. Conflicting conditions of the Client shall only apply if we have
                expressly agreed to them in writing.
              </p>

              <h3 className="font-semibold mt-4">2. Conclusion of Contract</h3>
              <p>
                Our offers are non-binding. A contract is concluded when the Client accepts our offer in writing
                (e.g. by email) or when we commence the provision of services at the explicit request of the Client.
              </p>

              <h3 className="font-semibold mt-4">3. Services of the Agency</h3>
              <p>
                The exact scope of services is defined in the respective offer, statement of work, or project contract.
                Our services are typically individual and project-based. Any changes, additions, or extra services
                beyond the agreed scope will be charged separately.
              </p>

              <h3 className="font-semibold mt-4">4. Client&apos;s Duties to Cooperate</h3>
              <p>
                The Client shall provide all information, materials, and access data necessary for the provision of
                the services (e.g. briefings, images, logos, texts, access to accounts) in a timely and complete manner.
                Delays caused by insufficient or late cooperation may extend timelines and result in additional costs
                to be borne by the Client.
              </p>

              <h3 className="font-semibold mt-4">5. Remuneration and Payment</h3>
              <p>
                All fees are agreed individually and are exclusive of applicable VAT, unless expressly stated otherwise.
              </p>
              <p>
                Unless otherwise agreed, invoices are payable within <strong>14 days</strong> from the invoice date,
                without deduction, by bank transfer to the account specified on the invoice.
              </p>
              <p>
                We may request reasonable down payments or instalments, especially for larger or long-term projects.
              </p>

              <h3 className="font-semibold mt-4">6. Usage Rights</h3>
              <p>
                Unless otherwise agreed, all copyrights and related rights to the works created by us (e.g. concepts,
                designs, photos, videos, texts, graphics) remain with us.
              </p>
              <p>
                Upon full payment of all agreed fees, the Client receives a{" "}
                <strong>simple, non-exclusive, worldwide and unlimited right of use</strong> for the contractually agreed
                purpose. Any use beyond this purpose, including editing, sublicensing or transfer to third parties,
                requires our prior written consent.
              </p>
              <p>
                Until full payment is received, all rights remain exclusively with us; the Client is not entitled to use
                or publish the content prior to full payment.
              </p>

              <h3 className="font-semibold mt-4">7. Acceptance and Revisions</h3>
              <p>
                After delivery of the services, the Client shall review the work within a reasonable period. If no
                objections are raised within 7 days, the work shall be deemed accepted.
              </p>
              <p>
                Unless otherwise agreed, our offers generally include a reasonable number of revision rounds.
                Additional revisions or fundamental conceptual changes beyond the initially agreed scope will be
                charged separately.
              </p>

              <h3 className="font-semibold mt-4">8. Liability</h3>
              <p>
                We are liable without limitation for intent and gross negligence as well as for injuries to life, body,
                or health. In cases of slight negligence, we are only liable for breaches of essential contractual
                obligations (cardinal duties) and limited to the foreseeable damage typical for the contract.
              </p>
              <p>
                We are not liable for indirect or consequential damages, lost profits or unforeseeable damage, to the
                extent permitted by law.
              </p>

              <h3 className="font-semibold mt-4">9. Reference Use</h3>
              <p>
                We may name the Client and the project as a reference in an appropriate manner (e.g. on our Website,
                social media or in presentations) and show the work created (e.g. screenshots, images, videos),
                unless the Client expressly objects.
              </p>

              <h3 className="font-semibold mt-4">10. Termination</h3>
              <p>
                Ongoing project agreements may be terminated by either party for cause without notice. Ordinary
                termination is only possible if expressly agreed in the contract.
              </p>
              <p>
                In the event of termination, the Client shall pay for all services rendered and costs incurred up to
                the effective date of termination. Rights already granted may revert to us if full payment has not
                been made.
              </p>

              <h3 className="font-semibold mt-4">11. Final Provisions</h3>
              <p>
                These Terms are governed by the laws of the Federal Republic of Germany, excluding the UN Convention
                on Contracts for the International Sale of Goods (CISG).
              </p>
              <p>
                If the Client is a merchant, the agreed place of jurisdiction &ndash; where legally permissible &ndash;
                is Frankfurt am Main, Germany. If any provision of these Terms is or becomes invalid, the remaining
                provisions shall remain in full force and effect.
              </p>

              <p>Last updated: November 2025</p>
            </div>
          </PageShell>
        )}


          {page === "datenschutz" && (
          <PageShell title="Datenschutz" onBack={goDesktop}>
            <div className="max-w-3xl mx-auto leading-relaxed text-sm md:text-base space-y-6 text-left">

              {/* ********** DEUTSCH ********** */}
              <h2 className="text-lg md:text-xl font-semibold text-center mb-4">
                Datenschutzerkl&auml;rung (DE)
              </h2>

              <p>
                Diese Datenschutzerkl&auml;rung informiert Sie dar&uuml;ber, wie wir personenbezogene Daten
                verarbeiten, wenn Sie unsere Website{" "}
                <strong>youplus.com</strong> (nachfolgend &bdquo;Website&ldquo;) besuchen.
              </p>

              <h3 className="font-semibold mt-4">1. Verantwortlicher</h3>
              <p>
                Verantwortlich im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:
                <br />
                <br />
                <strong>Y+ &ndash; a Brand from Socon</strong>
                <br />
                Socon UG (haftungsbeschr&auml;nkt)
                <br />
                Hanauer Landstra&szlig;e 521
                <br />
                60386 Frankfurt am Main
                <br />
                Deutschland
                <br />
                <br />
                Vertreten durch: Younes Mouhsine, Younes Mekki
                <br />
                E-Mail:{" "}
                <a href="mailto:contact@socon.ch" className="underline">
                  contact@socon.ch
                </a>
              </p>

              <h3 className="font-semibold mt-4">2. Hosting &amp; Server-Logfiles</h3>
              <p>
                Unsere Website wird bei einem externen Dienstleister gehostet:
                <br />
                <strong>dogado GmbH</strong> (&quot;Hosting-Provider&quot;).
              </p>
              <p>
                Beim Aufruf unserer Website verarbeitet der Hosting-Provider automatisch Server-Logfiles,
                die Ihr Browser &uuml;bermittelt. Dazu geh&ouml;ren insbesondere:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>IP-Adresse (gegebenenfalls gek&uuml;rzt/anonymisiert)</li>
                <li>Datum und Uhrzeit des Zugriffs</li>
                <li>aufgerufene Seite/Datei</li>
                <li>Referrer-URL (zuvor besuchte Seite)</li>
                <li>Browsertyp und -version</li>
                <li>Betriebssystem</li>
              </ul>
              <p>
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
                Unser berechtigtes Interesse besteht in der technischen Bereitstellung, Stabilit&auml;t und
                Sicherheit der Website.
              </p>

              <h3 className="font-semibold mt-4">3. Cookies, Tracking &amp; Analyse</h3>
              <p>
                Wir setzen derzeit <strong>keine</strong> Web-Analyse-Tools (z.&nbsp;B. Google Analytics),
                keine Marketing-Pixel (z.&nbsp;B. Facebook Pixel, TikTok Pixel) und keine Tracking-Cookies ein.
              </p>
              <p>
                Es werden nur technisch notwendige Daten verarbeitet, die f&uuml;r den Betrieb der Website
                erforderlich sind (siehe Server-Logfiles).
              </p>

              <h3 className="font-semibold mt-4">4. Kontakt per E-Mail</h3>
              <p>
                Wenn Sie uns per E-Mail kontaktieren, verarbeiten wir Ihre Angaben (insbesondere E-Mail-Adresse,
                Name, Inhalt der Nachricht) zur Bearbeitung Ihrer Anfrage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b
                DSGVO, sofern die Anfrage auf den Abschluss oder die Durchf&uuml;hrung eines Vertrages gerichtet ist,
                ansonsten Art. 6 Abs. 1 lit. f DSGVO.
              </p>
              <p>
                Die Daten werden gel&ouml;scht, sobald sie f&uuml;r die Bearbeitung Ihrer Anfrage nicht mehr
                erforderlich sind und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
              </p>

              <h3 className="font-semibold mt-4">5. Eingebettete/Vorgehaltene Videos</h3>
              <p>
                Auf unserer Website werden Videos eingebunden, die wir <strong>lokal auf unserem Server</strong> bzw.
                der Infrastruktur unseres Hosting-Providers speichern. Beim Abspielen dieser Videos werden keine
                Daten an externe Video-Plattformen (wie YouTube oder Vimeo) &uuml;bermittelt.
              </p>

              <h3 className="font-semibold mt-4">6. Social Media Links</h3>
              <p>
                Auf unserer Website verlinken wir auf unsere Auftritte bei Social-Media-Plattformen, z.&nbsp;B.:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Instagram</li>
                <li>TikTok</li>
                <li>YouTube</li>
                <li>Facebook</li>
                <li>LinkedIn</li>
              </ul>
              <p>
                Es handelt sich dabei um <strong>reine Links</strong>. Beim Besuch unserer Website werden
                keine Daten automatisch an diese Plattformen &uuml;bermittelt. Erst wenn Sie den jeweiligen Link
                anklicken, &ouml;ffnet sich die Seite des Drittanbieters und es gelten die Datenschutzbestimmungen
                des jeweiligen Anbieters.
              </p>

              <h3 className="font-semibold mt-4">7. Empf&auml;nger von Daten</h3>
              <p>
                Eine Weitergabe Ihrer personenbezogenen Daten an Dritte erfolgt nur, wenn:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Sie ausdr&uuml;cklich eingewilligt haben (Art. 6 Abs. 1 lit. a DSGVO),</li>
                <li>die Verarbeitung zur Vertragserf&uuml;llung erforderlich ist (Art. 6 Abs. 1 lit. b DSGVO),</li>
                <li>eine rechtliche Verpflichtung besteht (Art. 6 Abs. 1 lit. c DSGVO) oder</li>
                <li>ein berechtigtes Interesse besteht und keine &uuml;berwiegenden Interessen Ihrerseits entgegenstehen (Art. 6 Abs. 1 lit. f DSGVO).</li>
              </ul>

              <h3 className="font-semibold mt-4">8. Speicherdauer</h3>
              <p>
                Server-Logfiles werden in der Regel f&uuml;r einen Zeitraum von <strong>7 bis 14 Tagen</strong> gespeichert
                und anschlie&szlig;end gel&ouml;scht, sofern keine l&auml;ngere Speicherung aus Sicherheitsgr&uuml;nden
                (z.&nbsp;B. zur Aufkl&auml;rung von Missbrauchsf&auml;llen) erforderlich ist.
              </p>
              <p>
                E-Mail-Korrespondenz wird solange aufbewahrt, wie sie f&uuml;r die Kommunikation mit Ihnen bzw. zur
                Vertragsabwicklung erforderlich ist und gesetzlichen Aufbewahrungspflichten unterliegt.
              </p>

              <h3 className="font-semibold mt-4">9. Ihre Rechte nach der DSGVO</h3>
              <p>Sie haben im Rahmen der gesetzlichen Vorgaben folgende Rechte:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
                <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
                <li>Recht auf L&ouml;schung (Art. 17 DSGVO)</li>
                <li>Recht auf Einschr&auml;nkung der Verarbeitung (Art. 18 DSGVO)</li>
                <li>Recht auf Daten&uuml;bertragbarkeit (Art. 20 DSGVO)</li>
                <li>Recht auf Widerspruch (Art. 21 DSGVO)</li>
              </ul>
              <p>
                Zur Aus&uuml;bung Ihrer Rechte k&ouml;nnen Sie sich jederzeit an uns wenden:
                <br />
                E-Mail:{" "}
                <a href="mailto:contact@socon.ch" className="underline">
                  contact@socon.ch
                </a>
              </p>

              <h3 className="font-semibold mt-4">10. Aktualisierung dieser Datenschutzerkl&auml;rung</h3>
              <p>
                Wir behalten uns vor, diese Datenschutzerkl&auml;rung anzupassen, damit sie stets den aktuellen
                rechtlichen Anforderungen entspricht oder &Auml;nderungen unserer Leistungen abzubilden.
              </p>
              <p>Stand: November 2025</p>

              <hr className="my-10 border-neutral-300" />

              {/* ********** ENGLISCH ********** */}
              <h2 className="text-lg md:text-xl font-semibold text-center mb-4">
                Privacy Policy (EN)
              </h2>

              <p>
                This Privacy Policy explains how we process personal data when you visit our website{" "}
                <strong>youplus.com</strong> (&quot;Website&quot;).
              </p>

              <h3 className="font-semibold mt-4">1. Controller</h3>
              <p>
                The controller within the meaning of the GDPR is:
                <br />
                <br />
                <strong>Y+ &ndash; a Brand from Socon</strong>
                <br />
                Socon UG (haftungsbeschr&auml;nkt)
                <br />
                Hanauer Landstra&szlig;e 521
                <br />
                60386 Frankfurt am Main
                <br />
                Germany
                <br />
                <br />
                Represented by: Younes Mouhsine, Younes Mekki
                <br />
                Email:{" "}
                <a href="mailto:contact@socon.ch" className="underline">
                  contact@socon.ch
                </a>
              </p>

              <h3 className="font-semibold mt-4">2. Hosting &amp; Server Log Files</h3>
              <p>
                Our Website is hosted by an external provider, <strong>dogado GmbH</strong>.
                When you access our Website, the hosting provider automatically processes server log files,
                including:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>IP address (shortened/anonymised where applicable)</li>
                <li>Date and time of access</li>
                <li>Requested page/file</li>
                <li>Referrer URL</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
              </ul>
              <p>
                The legal basis for this processing is Article 6(1)(f) GDPR. Our legitimate interest is to
                provide a stable, secure and technically functioning Website.
              </p>

              <h3 className="font-semibold mt-4">3. Cookies, Tracking &amp; Analytics</h3>
              <p>
                We currently do <strong>not</strong> use web analytics tools (such as Google Analytics),
                marketing pixels (e.g. Meta/Facebook or TikTok) or non-essential cookies.
              </p>
              <p>
                Only data that is technically necessary to operate the Website (see server log files) is processed.
              </p>

              <h3 className="font-semibold mt-4">4. Contact via Email</h3>
              <p>
                If you contact us via email, we process your data (in particular email address, name and content
                of your message) solely to respond to your enquiry. The legal basis is Article 6(1)(b) GDPR
                (performance of a contract or pre-contractual measures) or Article 6(1)(f) GDPR (our legitimate
                interest in answering enquiries).
              </p>

              <h3 className="font-semibold mt-4">5. Hosted Videos</h3>
              <p>
                Videos provided on our Website are hosted <strong>locally</strong> on our own server or within
                our hosting infrastructure. No data is transferred to third-party video platforms when you watch
                these videos.
              </p>

              <h3 className="font-semibold mt-4">6. Social Media Links</h3>
              <p>
                We include links to our profiles on social media platforms (e.g. Instagram, TikTok, YouTube,
                Facebook, LinkedIn). These are simple hyperlinks.
              </p>
              <p>
                No data is sent to these platforms automatically when you visit our Website. Only when you click
                the respective link will you be redirected to the provider&apos;s website, where their own privacy
                policy applies.
              </p>

              <h3 className="font-semibold mt-4">7. Recipients</h3>
              <p>
                We only share personal data with third parties if:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>you have given your explicit consent (Art. 6(1)(a) GDPR),</li>
                <li>it is necessary for the performance of a contract (Art. 6(1)(b) GDPR),</li>
                <li>we are legally obliged to do so (Art. 6(1)(c) GDPR), or</li>
                <li>there is a legitimate interest and no overriding interests on your side (Art. 6(1)(f) GDPR).</li>
              </ul>

              <h3 className="font-semibold mt-4">8. Retention Period</h3>
              <p>
                Server log files are usually stored for <strong>7 to 14 days</strong> and then deleted,
                unless a longer retention is required for security reasons (e.g. to investigate misuse).
              </p>
              <p>
                Email correspondence is stored as long as necessary to process your request and to comply with
                statutory retention periods.
              </p>

              <h3 className="font-semibold mt-4">9. Your Rights</h3>
              <p>
                Under the GDPR you have the following rights:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Right of access</li>
                <li>Right to rectification</li>
                <li>Right to erasure</li>
                <li>Right to restriction of processing</li>
                <li>Right to data portability</li>
                <li>Right to object</li>
              </ul>
              <p>
                To exercise these rights, please contact:
                <br />
                Email:{" "}
                <a href="mailto:contact@socon.ch" className="underline">
                  contact@socon.ch
                </a>
              </p>

              <h3 className="font-semibold mt-4">10. Changes to this Policy</h3>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in legal requirements
                or our services.
              </p>
              <p>Last updated: November 2025</p>
            </div>
          </PageShell>
        )}


          {page === "impressum" && (
            <PageShell title="Impressum" onBack={goDesktop}>
              <div className="max-w-3xl mx-auto leading-relaxed text-sm md:text-base space-y-6 text-center">

                <p><strong>Angaben gemäß § 5 TMG</strong></p>

                <p>
                  <strong>Y+ – a Brand from Socon</strong><br />
                  Socon UG (haftungsbeschränkt)<br />
                  Hanauer Landstraße 521<br />
                  60386 Frankfurt am Main
                </p>

                <p>
                  Handelsregister: HRB 136742<br />
                  Registergericht: Amtsgericht Frankfurt
                </p>

                <p>
                  <strong>Vertreten durch:</strong><br />
                  Younes Mouhsine<br />
                  Younes Mekki
                </p>

                <p>
                  <strong>Kontakt</strong><br />
                  E-Mail: Contact@yplus.co
                </p>


                <p>
                  <strong>EU-Streitschlichtung</strong><br />
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:<br />
                  <a
                    className="underline"
                    href="https://ec.europa.eu/consumers/ord"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://ec.europa.eu/consumers/ord
                  </a><br />
                  Unsere E-Mail-Adresse finden Sie oben im Impressum.
                </p>

                <p>
                  <strong>
                    Verbraucherstreitbeilegung / Universalschlichtungsstelle
                  </strong>
                  <br />
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren
                  vor einer Verbraucherschlichtungsstelle teilzunehmen.
                </p>

                <p>
                  <strong>Haftung für Inhalte</strong><br />
                  Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die
                  Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch
                  keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG
                  für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen
                  verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht
                  verpflichtet, übermittelte oder gespeicherte fremde Informationen zu
                  überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
                  Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen
                  nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche
                  Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten
                  Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden
                  Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
                </p>

                <p>
                  <strong>Haftung für Links</strong><br />
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte
                  wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch
                  keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der
                  jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten
                  Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße
                  überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht
                  erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist
                  jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar.
                  Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend
                  entfernen.
                </p>

                <p>
                  <strong>Urheberrecht</strong><br />
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten
                  unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung,
                  Verbreitung und jede Art der Verwertung außerhalb der Grenzen des
                  Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors
                  bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten,
                  nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite
                  nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter
                  beachtet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam
                  werden, bitten wir um entsprechenden Hinweis. Bei Bekanntwerden von
                  Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
                </p>

                <p>
                  <strong>Dieses Impressum gilt auch für folgende Online-Auftritte:</strong><br />
                  Instagram:{" "}
                  <a
                    className="underline"
                    href="https://www.instagram.com/soconstudio/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.instagram.com/soconstudio/
                  </a>
                  <br />
                  LinkedIn:{" "}
                  <a
                    className="underline"
                    href="https://www.linkedin.com/company/socongroup/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.linkedin.com/company/socongroup/
                  </a>
                  <br />
                  YouTube:{" "}
                  <a
                    className="underline"
                    href="https://www.youtube.com/@SoconStudio"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.youtube.com/@SoconStudio
                  </a>
                  <br />
                  TikTok:{" "}
                  <a
                    className="underline"
                    href="https://www.tiktok.com/@soconstudio?lang=de-DE"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.tiktok.com/@soconstudio?lang=de-DE
                  </a>
                </p>

              </div>
            </PageShell>
          )}


          {page === "team" && (
            <PageShell title="about us" onBack={goDesktop}>
              <div className="bg-white text-neutral-900 rounded-xl max-w-3xl mx-auto p-0 overflow-hidden">
                <TeamContent onClose={goDesktop} />
              </div>
            </PageShell>
          )}

          {page === "profiles" && (
            <PageShell title="Profiles" onBack={goDesktop}>
              <div className="bg-white text-neutral-900 rounded-xl max-w-3xl mx-auto p-0 overflow-hidden">
                <ProfilesContent onClose={goDesktop} />
              </div>
            </PageShell>
          )}
        </>
      )}
    </div>
  );
}

export default App;
